import { useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useCreateForm } from "./use-create-form";

// Ports components/admin/forms/CreateFormButton.tsx (legacy Next.js dialog
// hosting the createFormAction). Two variants — default (header pill) and
// empty (full button shown inside the empty-state card). On success the
// caller is navigated to /admin/forms/:id/edit.

export function CreateFormDialog({
  variant = "default",
}: {
  variant?: "default" | "empty";
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const mutation = useCreateForm();

  function reset() {
    setName("");
    setDescription("");
  }

  function onOpenChange(next: boolean) {
    if (!next) reset();
    setOpen(next);
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    mutation.mutate(
      {
        name: trimmedName,
        description: description.trim() ? description.trim() : null,
      },
      {
        onSuccess: (res) => {
          setOpen(false);
          reset();
          toast.success("Form created");
          navigate(`/admin/forms/${res.id}/edit`);
        },
        onError: (err) => {
          // useCreateForm already toasts, but it uses the raw error.message.
          // Override with the zod-aware extractor so slug-collision retries
          // and validation errors both surface cleanly.
          toast.error(formatApiError(err, "Failed to create form"));
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {variant === "empty" ? (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create your first form
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New form
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">New form</DialogTitle>
          <DialogDescription>
            Give your form a name. You can edit everything else in the builder.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Form name</Label>
            <Input
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Partner application"
              required
              maxLength={160}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              We&apos;ll derive a slug from this — you can rename without breaking links.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short note shown above the form"
              rows={3}
              maxLength={2000}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !name.trim()}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create form
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
